import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import {
  countPhotosByStepId,
  isStepMarkedComplete,
  isStepRequiredDone,
  PhotoStep,
  Session,
} from '@transpordiamet/shared';
import { getInitialLocale } from '../i18n';
import type { PermissionStatus } from './permissions';

/**
 * Locale State
 * Tracks the current active locale for i18n
 * Initialized from URL query parameter to prevent double API calls
 */
export const localeAtom = atom<string>(getInitialLocale());

/**
 * Session State
 * Tracks the current photo capture session data
 */
export const sessionAtom = atom<Session | null>(null);

/**
 * Photo returned by the native camera, kept above the locale remount.
 * Step state is wiped when I18nProvider remounts; this file is not.
 */
export type PendingCapture = {
  sessionId: string;
  stepId: string;
  file: File;
  capturedAt: string;
  /** Taken by the in-page camera: fresh, landscape and inside the capture box already. */
  inPage?: true;
};

export const pendingCaptureAtom = atom<PendingCapture | null>(null);

/**
 * Per-session "Continue on this device" overrides (desktop → capture flow).
 * sessionStorage so the escape hatch lasts for the tab but not forever.
 */
const forceMobileStorage = createJSONStorage<Record<string, true>>(() => sessionStorage);

export const forceMobileSessionsAtom = atomWithStorage<Record<string, true>>(
  'force-mobile-sessions',
  {},
  forceMobileStorage
);

/**
 * Last Photo Timestamp
 * Derived from session photos - automatically computes the timestamp of the most recent photo
 */
function timeMs(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

export const lastPhotoTimestampAtom = atom<string | null>((get) => {
  const session = get(sessionAtom);
  const photos = session?.photos ?? [];
  const latestPhoto = photos.reduce<number | null>((latest, photo) => {
    const time = timeMs(photo.createdAt);
    if (time == null) return latest;
    return latest == null || time > latest ? time : latest;
  }, null);
  return latestPhoto == null ? null : new Date(latestPhoto).toISOString();
});

/** Step IDs whose photo count passes `isDone`; missing photos count as zero. */
const stepIdsWhere = (isDone: (step: PhotoStep, count: number) => boolean) =>
  atom<Set<string>>((get) => {
    const session = get(sessionAtom);
    const photoCountByStepId = countPhotosByStepId(session?.photos);
    return new Set(
      (session?.configuration?.steps ?? [])
        .filter((step) => isDone(step, photoCountByStepId[step.id] ?? 0))
        .map((step) => step.id)
    );
  });

/** Steps that met their required photoCount (extras ignored). Drives navigation. */
export const completedStepIdsAtom = stepIdsWhere(isStepRequiredDone);

/**
 * Steps that show a checkmark in the overview and the capture sidebar.
 * Optional steps (photoCount 0) stay numbered until a photo exists.
 * Do not use this for navigation — that stays on completedStepIdsAtom.
 */
export const markedCompleteStepIdsAtom = stepIdsWhere(isStepMarkedComplete);

/**
 * First Incomplete Step
 * Derived from session configuration and completed steps - finds the first step without a photo
 */
export const firstIncompleteStepAtom = atom((get) => {
  const session = get(sessionAtom);
  const completedStepIds = get(completedStepIdsAtom);
  return session?.configuration?.steps?.find((step) => !completedStepIds.has(step.id));
});

/**
 * Step By ID Atom Factory
 * Creates a derived atom that finds a step by its ID
 * @param stepId - The step ID to look up
 */
export const stepByIdAtom = (stepId: string | undefined) =>
  atom((get) => {
    const session = get(sessionAtom);
    return session?.configuration?.steps?.find((step) => step.id === stepId);
  });

/**
 * Step Index Atom Factory
 * Creates a derived atom that finds the index of a step by its ID
 * @param stepId - The step ID to look up
 */
export const stepIndexAtom = (stepId: string | undefined) =>
  atom((get) => {
    const session = get(sessionAtom);
    return session?.configuration?.steps?.findIndex((step) => step.id === stepId) ?? -1;
  });

/**
 * Photos By Step ID Atom Factory
 * Creates a derived atom that returns all photos for a specific step
 * @param stepId - The step ID to filter by
 */
export const photosByStepIdAtom = (stepId: string | undefined) =>
  atom((get) => {
    if (!stepId) return [];
    const session = get(sessionAtom);
    return session?.photos?.filter((photo) => photo.stepId === stepId) || [];
  });

/**
 * Remaining Seconds Until Session Timeout
 * Tracks countdown timer for session restart (PHOTO_GAP_TIMEOUT_SECONDS from last photo)
 * Updated by useSessionTimeout hook, read by SessionTimer component
 */
export const remainingSecondsAtom = atom<number | null>(null);

/**
 * Permissions Overlay State
 * Controls visibility of the permissions request overlay
 */
export const showPermissionsOverlayAtom = atom<boolean>(false);

/**
 * The page's last permission check, so the overlay lists only what failed. Null
 * until checked (iOS shows the overlay first, since its prompts need a tap):
 * both are listed then.
 */
export const permissionStatusAtom = atom<PermissionStatus | null>(null);
